import { useEffect, useState } from "react";
import { CheckCircle2, Globe, Loader2, XCircle } from "lucide-react";
import { api } from "../../lib/api";

// Carte « Indexation Google » (demande du propriétaire, 2026-10-06) : suivi de
// l'indexation depuis le 2 octobre 2026 — état du sitemap dans Search Console
// (pages soumises vs indexées) et pages VUES dans Google (une page avec des
// impressions EST indexée), dont le décompte des pages villes SEO.
export default function IndexationCard() {
  const [d, setD] = useState(null);   // null = chargement ; {connecte:false} = non branché
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/admin/seo/indexation")
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-4" data-testid="indexation-card">
      <p className="font-semibold flex items-center gap-2">
        <Globe className="w-4 h-4 text-gold" /> Indexation Google
        <span className="text-[9px] uppercase tracking-widest border border-ivory/20 text-ivory/50 rounded-full px-2 py-0.5">
          depuis le 2 oct. 2026
        </span>
      </p>
      <p className="text-xs text-ivory/70 mt-1 mb-4">
        Suivi des pages villes et du sitemap : une page qui obtient des vues dans Google est indexée.
      </p>
      {err ? (
        <p className="text-sm text-red-300" data-testid="indexation-error">Suivi indisponible — réessayez.</p>
      ) : !d ? (
        <p className="text-sm text-ivory/50" data-testid="indexation-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Lecture de Search Console…
        </p>
      ) : d.connecte === false ? (
        <p className="text-sm text-amber-300" data-testid="indexation-not-connected">
          Google Search Console n'est pas connecté sur ce site — branchez-le dans la carte « Suivi SEO » ci-dessus.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Pages indexées (sitemap)</p>
              <p className="font-serif text-2xl mt-1 text-gold" data-testid="indexation-indexees">
                {d.sitemap ? d.sitemap.indexes.toLocaleString("fr-CA") : "—"}
              </p>
              {d.sitemap && (
                <p className="text-[10px] text-ivory/45 mt-0.5">
                  sur {d.sitemap.soumis.toLocaleString("fr-CA")} soumises · lu le {d.sitemap.derniere_lecture || "—"}
                </p>
              )}
              {!d.sitemap && (
                <p className="text-[10px] text-amber-300/80 mt-0.5">sitemap non inscrit dans Search Console</p>
              )}
            </div>
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Pages vues dans Google</p>
              <p className="font-serif text-2xl mt-1 text-ivory" data-testid="indexation-pages-vues">
                {d.pages_vues ?? "—"}
              </p>
              <p className="text-[10px] text-ivory/45 mt-0.5">avec au moins une vue depuis le 2 oct.</p>
            </div>
            <div className="bg-ink border border-emerald-400/25 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Pages villes vues</p>
              <p className="font-serif text-2xl mt-1 text-emerald-300" data-testid="indexation-villes">
                {d.pages_villes_vues ?? "—"}<span className="text-sm text-ivory/50"> / {d.villes_total ?? "—"}</span>
              </p>
              <p className="text-[10px] text-ivory/45 mt-0.5">pages villes SEO apparues dans Google</p>
            </div>
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Santé du sitemap</p>
              <p className="font-serif text-lg mt-1.5" data-testid="indexation-sante">
                {d.sitemap ? (
                  d.sitemap.erreurs === 0
                    ? <span className="text-emerald-300 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Aucune erreur</span>
                    : <span className="text-red-300 inline-flex items-center gap-1.5"><XCircle className="w-4 h-4" /> {d.sitemap.erreurs} erreur(s)</span>
                ) : <span className="text-ivory/45">—</span>}
              </p>
              {d.sitemap?.avertissements > 0 && (
                <p className="text-[10px] text-amber-300/80 mt-0.5">{d.sitemap.avertissements} avertissement(s)</p>
              )}
            </div>
          </div>
          {(d.top_pages || []).length > 0 && (
            <div data-testid="indexation-top-pages">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-2">Pages les plus vues depuis le 2 oct.</p>
              <ul className="space-y-1">
                {d.top_pages.map((p) => (
                  <li key={p.url} className="text-xs text-ivory/60 flex gap-3 items-baseline">
                    <span className="truncate max-w-[70%] text-ivory/80">{p.url.replace(/^https?:\/\/[^/]+/, "")}</span>
                    <span className="ml-auto text-gold/90 shrink-0">{p.vues.toLocaleString("fr-CA")} vue{p.vues > 1 ? "s" : ""}</span>
                    <span className="text-ivory/45 shrink-0">{p.clics} clic{p.clics > 1 ? "s" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
